/**
 * @file use-stepper-fit.ts
 * @description Ширина точки шкалы «Путь заказа» под ширину блока
 * (phase19.31, приёмка qa4 п. 10).
 *
 * Было: точка фиксированной ширины (64 / 96px), шкала прокручивалась к
 * текущей стадии по центру — и на 1280 первая стадия обрезалась пополам
 * («ование авки»): линия пути «уезжала» под левый край.
 *
 * Стало: ширину точки считаем от ширины шкалы так, чтобы в окне помещалось
 * ЦЕЛОЕ число стадий. Все влезают — делят ширину поровну, прокрутки нет.
 * Не влезают — точка шириной «окно / сколько влезает целиком», а прокрутка
 * идёт шагом в одну точку (`snapScroll`): у края всегда целая стадия, а не
 * половина подписи; что за краем — показывает затухание края. Ручная
 * прокрутка пальцем тоже прилипает к стадии: у шкалы `snap-x`, у точки
 * `snap-start` (`OrderChainStepper`).
 * Логику блока (какие стадии, где текущая) хук не трогает.
 */

import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Ширина одной точки в пикселях или `null`, пока шкала не измерена (тесты,
 * первый кадр) — тогда действуют классы ширины по умолчанию.
 */
export function useStepperFit(
  listRef: RefObject<HTMLElement | null>,
  count: number,
  minWidth: number,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || count === 0) return;
    const measure = () => {
      const box = list.clientWidth;
      if (box <= 0) {
        setWidth(null);
        return;
      }
      const fit = Math.max(1, Math.floor(box / minWidth));
      // Целые пиксели: дробная ширина копит погрешность к концу шкалы.
      const next = Math.floor(box / Math.min(count, fit));
      setWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    // В jsdom наблюдателя размеров нет — хватит первого замера.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, count, minWidth]);

  return width;
}

/** Позиция прокрутки, кратная ширине точки: у края — целая стадия. */
export function snapScroll(left: number, step: number | null, max: number): number {
  if (!step) return left;
  const snapped = Math.round(left / step) * step;
  // Последний шаг может не делиться нацело (остаток ширины) — упираемся в край.
  return Math.max(0, Math.min(snapped, max));
}
